import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { stats, SteamGame } from '@/lib/api'
import { SteamIcon, FortniteIcon, XboxIcon } from '@/components/PlatformIcons'

const PLATFORMS = [
  {
    key: 'steam' as const,
    name: 'Steam',
    color: '#66c0f4',
    link: '/steam',
    Icon: SteamIcon,
  },
  {
    key: 'fortnite' as const,
    name: 'Fortnite',
    color: '#9d4dbb',
    link: '/fortnite',
    Icon: FortniteIcon,
  },
  {
    key: 'xbox' as const,
    name: 'Xbox',
    color: '#107c10',
    link: '/xbox',
    Icon: XboxIcon,
  },
] as const

export function StatsOverview() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['stats'],
    queryFn: async () => {
      const result = await stats.getAll()
      return result.data
    },
  })

  if (isLoading) {
    return (
      <div className="container mx-auto max-w-5xl px-4 py-16">
        <div className="flex justify-center items-center min-h-[40vh]" role="status" aria-busy="true">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-500" />
          <span className="sr-only">Loading stats...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="container mx-auto max-w-5xl px-4 py-16 text-center">
        <p className="text-red-400 mb-4">Failed to load stats.</p>
        <button
          onClick={() => refetch()}
          className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm font-medium transition-colors"
        >
          Try Again
        </button>
      </div>
    )
  }

  const steamGames = data?.steam?.games?.length ?? 0
  const steamHours = data?.steam?.games
    ? Math.round(data.steam.games.reduce((sum, g) => sum + g.playtimeForever, 0) / 60)
    : 0
  const gamerscore = data?.xbox?.gamerscore ?? 0
  const fortniteWins = data?.fortnite?.stats?.overall?.wins ?? 0
  const fortniteKd = data?.fortnite?.stats?.overall?.kd ?? 0
  const trackedPlatforms = [data?.steam, data?.fortnite, data?.xbox].filter(Boolean).length

  const recentSteamGames = data?.steam?.recentlyPlayed?.slice(0, 6) ?? []

  const platformStats: Record<string, { label: string; value: string }[]> = {
    steam: [
      { label: 'games', value: `${steamGames}` },
      { label: 'hours', value: steamHours.toLocaleString() },
    ],
    fortnite: [
      { label: 'wins', value: `${fortniteWins}` },
      { label: 'K/D', value: fortniteKd.toFixed(2) },
      { label: 'matches', value: `${data?.fortnite?.stats?.overall?.matches ?? 0}` },
    ],
    xbox: [
      { label: 'gamerscore', value: gamerscore.toLocaleString() },
      { label: 'games', value: `${(data?.xbox?.recentGames ?? []).filter((g: any) => g.maxGamerscore > 0).length}` },
    ],
  }

  const quickStats = [
    { label: 'Total Games', value: steamGames.toLocaleString(), color: '#66c0f4' },
    { label: 'Total Hours', value: steamHours.toLocaleString(), color: '#66c0f4' },
    { label: 'Gamerscore', value: gamerscore.toLocaleString(), color: '#107c10' },
    { label: 'Fortnite Wins', value: `${fortniteWins}`, color: '#9d4dbb' },
    { label: 'Fortnite K/D', value: fortniteKd.toFixed(2), color: '#9d4dbb' },
    { label: 'Platforms Tracked', value: `${trackedPlatforms}`, color: '#9ca3af' },
  ]

  return (
    <div className="container mx-auto max-w-5xl px-4 py-8">
      {/* Header */}
      <h1 className="text-4xl font-display font-bold mb-1">Stats Overview</h1>
      <p className="text-gray-400 mb-8">Your gaming stats across all platforms</p>

      {/* Platform Cards */}
      <div className="space-y-3 mb-10">
        {PLATFORMS.map(({ key, name, color, link, Icon }) => {
          const connected = !!data?.[key]
          return (
            <Link
              key={key}
              to={link}
              className="group block rounded-xl border border-gray-800 hover:border-opacity-100 transition-all overflow-hidden"
              style={{ borderColor: connected ? `${color}25` : undefined }}
            >
              <div className="h-1" style={{ backgroundColor: connected ? color : '#374151' }} />
              <div className="p-5">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-3 min-w-[140px]">
                    <span style={{ color: connected ? color : '#6b7280' }}>
                      <Icon size={18} />
                    </span>
                    <span className="font-bold text-lg">{name}</span>
                  </div>

                  {connected && platformStats[key] && (
                    <div className="flex gap-5">
                      {platformStats[key].map((s) => (
                        <div key={s.label}>
                          <span className="text-xl font-bold" style={{ color }}>{s.value}</span>
                          <span className="text-xs text-gray-500 ml-1.5">{s.label}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {!connected && (
                    <span className="text-sm text-gray-600">No data</span>
                  )}

                  <div className="ml-auto flex items-center gap-2 text-gray-600 group-hover:text-gray-300 transition-colors">
                    <span className="text-sm hidden sm:inline">View details</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </div>
            </Link>
          )
        })}
      </div>

      {/* Quick Stats Grid */}
      {trackedPlatforms > 0 && (
        <section className="mb-10">
          <h2 className="text-2xl font-display font-bold mb-4">Quick Stats</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {quickStats.map((stat) => (
              <div
                key={stat.label}
                className="bg-gray-800/50 rounded-xl border border-gray-800 p-4"
              >
                <div className="text-2xl font-bold" style={{ color: stat.color }}>
                  {stat.value}
                </div>
                <div className="text-xs text-gray-500 uppercase tracking-wider mt-1">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Recently Played */}
      {recentSteamGames.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-display font-bold">Recently Played</h2>
            <Link to="/steam" className="text-sm text-gray-500 hover:text-gray-300 transition-colors">
              View all
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {recentSteamGames.map((game: SteamGame) => (
              <div
                key={game.appId}
                className="group overflow-hidden rounded-xl border border-gray-800 hover:border-[#66c0f4]/30 transition-colors bg-gray-800/50"
              >
                <div className="relative h-28 overflow-hidden bg-gray-900">
                  <img
                    src={`https://cdn.cloudflare.steamstatic.com/steam/apps/${game.appId}/header.jpg`}
                    alt=""
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-transparent" />
                </div>
                <div className="p-3">
                  <h3 className="font-semibold text-sm truncate mb-1.5">{game.name}</h3>
                  <div className="flex gap-4 text-xs text-gray-500">
                    <span>
                      <span className="font-bold text-[#66c0f4]">{Math.round(game.playtimeForever / 60)}</span> hrs total
                    </span>
                    {game.playtimeRecent != null && game.playtimeRecent > 0 && (
                      <span>
                        <span className="font-bold text-[#66c0f4]">{Math.round(game.playtimeRecent / 60)}</span> hrs recent
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Empty State */}
      {!data?.steam && !data?.fortnite && !data?.xbox && (
        <div className="text-center py-16">
          <h2 className="text-2xl font-display font-bold mb-2">No Stats Available</h2>
          <p className="text-gray-500">
            Gaming accounts are being configured. Check back later.
          </p>
        </div>
      )}
    </div>
  )
}
